import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { endOfDay, startOfDay } from 'date-fns';

import { ctxReport, ReportContextType } from '@/contexts/ReportCtx';
import { ctxUser } from '@/contexts/UserContext';
import TargetTab from './TargetTab';
import DriverTab from './DriverTab';
import OverviewTab from './OverviewTab';
import { getMaxTargetNumber } from '@/generated/client/reports/reports';
import CategorySelector from './CategorySelector';
import DateSelectorTooltip from '@/components/common/date/DateRangeSelectTooltip';
import { Popup } from '@/components';
import './ReportPopup.css';
import { ReportMobileHeader } from './ReportMobileHeader';
import { ReportMobileBody } from './ReportMobileBody';
import { ReportMobileFooter } from './ReportMobileFooter';

export type ReportTabs = 'TargetTab' | 'DriverTab' | 'TargetReportTab' | 'DriverReportTab' | 'StatusTab';

function ReportDesktop({
  activeTab,
  setActiveTab,
  validDate,
  reportCtx,
}: {
  activeTab: ReportTabs;
  setActiveTab: (tab: ReportTabs) => void;
  validDate: boolean;
  reportCtx: ReportContextType;
}) {
  const { t } = useTranslation();

  const resetContext = useCallback(() => {
    reportCtx?.setSelectedTargets([]);
    reportCtx?.setSelectedDrivers([]);
    reportCtx?.setSelectedTemplateIds([]);
    reportCtx?.setSearchText(undefined);
  }, [reportCtx]);

  function onAccept(newFrom: Date | undefined, newTo: Date | undefined) {
    if (!newFrom || isNaN(newFrom.getTime()) || !newTo || isNaN(newTo.getTime())) {
      reportCtx?.setSelectedDates(undefined);
      return;
    }
    const from = startOfDay(newFrom);
    const to = endOfDay(newTo);
    reportCtx?.setSelectedDates({ from, to });
  }

  function updateActiveTab(tab: ReportTabs) {
    resetContext();
    setActiveTab(tab);
  }

  return (
    <div id="report-main-container">
      <div className="report-date-selector-container">
        <div>
          <h3 className="report-header">{t('report.dateSelector')}</h3>
          <span className="report-header-description">{t('report.dateSelectorDescription')}</span>
        </div>
        <DateSelectorTooltip validDate={validDate} onAccept={onAccept} selectedDates={reportCtx.selectedDates} />
      </div>

      <div className={`report-main-container ${validDate ? '' : 'disabled'}`} inert={!validDate}>
        <div className="report-category-selector-container">
          <div>
            <h3 className="report-header">{t('report.categorySelector')}</h3>
            <span className="report-header-description">{t('report.categorySelectorDescription')}</span>
          </div>
          <CategorySelector activeTab={activeTab} setActiveTab={updateActiveTab} />
        </div>

        {activeTab === 'TargetTab' && <TargetTab />}
        {activeTab === 'DriverTab' && <DriverTab />}
        {activeTab === 'StatusTab' && <OverviewTab />}
      </div>
    </div>
  );
}

export default function ReportPopup({ closePopup, isBottom }: { closePopup: () => Promise<void>; isBottom: boolean }) {
  const reportCtx = useContext(ctxReport);
  const userCtx = useContext(ctxUser);
  const [activeTab, setActiveTab] = useState<ReportTabs>('TargetTab');
  const [validDate, setValidDate] = useState<boolean>(false);

  const resetContext = useCallback(() => {
    reportCtx?.setSelectedTargets([]);
    reportCtx?.setSelectedDrivers([]);
    reportCtx?.setSelectedTemplateIds([]);
    reportCtx?.setSearchText(undefined);
  }, [reportCtx]);

  useEffect(() => {
    if (!userCtx || !reportCtx) return;
    const email = userCtx.user.email;
    if (email) {
      reportCtx.setAddressee(email);
    }
    resetContext();
  }, [userCtx]);

  useEffect(() => {
    async function loadMaxTargetNumber() {
      const localMax = await getMaxTargetNumber();
      reportCtx?.setMaxTargetNumber(localMax.data);
    }
    void loadMaxTargetNumber();
  }, []);

  useEffect(() => {
    const dateInvalid =
      reportCtx?.selectedDates == undefined ||
      reportCtx?.selectedDates.from == undefined ||
      reportCtx?.selectedDates.to == undefined;
    setValidDate(!dateInvalid);
  }, [reportCtx?.selectedDates]);

  if (!reportCtx || !userCtx || !closePopup) return null;

  return isBottom ? (
    <Popup
      popupTitle={undefined}
      header={<ReportMobileHeader activeTab={activeTab} setActiveTab={setActiveTab} />}
      body={<ReportMobileBody activeTab={activeTab} setActiveTab={setActiveTab} />}
      footer={<ReportMobileFooter activeTab={activeTab} setActiveTab={setActiveTab} />}
      closePopup={closePopup}
      showCloseButton={false}
      isFullScr={true}
    />
  ) : (
    <ReportDesktop activeTab={activeTab} setActiveTab={setActiveTab} validDate={validDate} reportCtx={reportCtx} />
  );
}
